import { ILiveClassStatSortableFields } from '@sdk/contracts';
import { useQuery } from '@tanstack/react-query';
import { SorterResult } from 'antd/es/table/interface';
import { create } from 'zustand';

import { useEventFilters } from '@/hooks';
import { sdk } from '@/services';

const INITIAL_PAGE = 1;
const INITIAL_LIMIT = 10;

const EventService = sdk.getService('EventService');

interface IStore {
  page: number;
  limit: number;
  setPageAndLimit: (page: number, limit: number) => void;
  sort?: SorterResult<ILiveClassStatSortableFields>;
  setSort: (sort?: SorterResult<ILiveClassStatSortableFields>) => void;
}

const store = create<IStore>(set => ({
  page: INITIAL_PAGE,
  limit: INITIAL_LIMIT,
  sort: undefined,
  setPageAndLimit: (page: number, limit: number) => set(state => ({ ...state, page, limit })),
  setSort: (sort?: SorterResult<ILiveClassStatSortableFields>) => set(state => ({ ...state, sort }))
}));

export const useLiveClassesStatsList = () => {
  const { page, limit, sort, setPageAndLimit, setSort } = store();
  const { filters } = useEventFilters();

  const queryKey = [
    'events',
    'teachers-with-stats',
    `page-${page}`,
    `limit-${limit}`,
    filters,
    sort
  ];
  const { isLoading, data, error } = useQuery({
    queryKey,
    queryFn: () =>
      EventService.findAllLiveClassesStats(
        { page, limit },
        filters,
        sort
          ? {
              order: sort.order ?? undefined,
              field: Array.isArray(sort.field)
                ? sort.field.join('.')
                : (sort.field as string | number | undefined)
            }
          : undefined
      )
  });

  const pagination = data && {
    page: data.page,
    limit: data.limit,
    hasNextPage: data.hasNextPage,
    hasPrevPage: data.hasPrevPage,
    nextPage: data.nextPage,
    pagingCounter: data.pagingCounter,
    prevPage: data.prevPage,
    totalDocs: data.totalDocs,
    totalPages: data.totalPages
  };

  return {
    liveClassesStats: data?.docs,
    isLoading,
    error,
    setPageAndLimit,
    pagination,
    sort,
    setSort
  };
};
